import {
  Suspense,
  useEffect,
  useMemo,
  useState,
  useRef,
  type SyntheticEvent,
} from "react";
import {
  Link,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { Languages } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@a2z/ui";
import { switchPrivateStore } from "@/shared/privateSessionChange";
import {
  capturePrivateSession,
  assertPrivateSessionLifetime,
} from "@/lib/api/private-session";
import { usePrivateToast } from "@/shared/usePrivateToast";
import { queryKeys } from "@/shared/queries";
import { endpoints } from "@/lib/api/endpoints";
import { useI18n, useTranslate } from "@/lib/i18n";
import { catalogs } from "@/lib/i18n/messages";
import { useErrorMessage } from "@/shared/errors";
import {
  PERMISSIONS,
  hasPermission,
  useSession,
} from "@/modules/auth/useSession";
import { useLogout } from "@/modules/auth/useLogout";
import { useApiQuery } from "@/shared/queries";
import type { RestaurantSettings } from "@/lib/api/types";
import { ManageBottomNav } from "./ManageBottomNav";
import {
  PageActionProvider,
  useMobilePageAction,
  useShellPageMeta,
} from "./PageActionSlot";
import { NavIcon } from "./navIcons";
import { ManageSidebar } from "./ManageSidebar";
import { isPaperNavKey, type PaperNavKey } from "./paperNav";
import type { ManageNavItem } from "./nav";
import { visibleManageRows } from "./rows";
import { holdsAdministrationPermission } from "@/modules/auth/backOffice";
import { useApplyDashboardTheme } from "@/app/themes/dashboardTheme";
import { usePosTheme } from "@/shared/posTheme";
import { RestaurantNotificationBell } from "@/modules/notifications/NotificationBell";
import { useNetworkStatus } from "@/lib/api/network";
import { OfflinePane } from "@/shared/systemScreens/OfflinePane";
import { usePageLoadingBox } from "@/shared/systemScreens/usePageLoadingBox";
import {
  pendingPage,
  prefetchPagesNow,
  prefetchPagesWhenIdle,
} from "@/app/lazyPage";
import { PageLoadingAnchor } from "./PageLoadingAnchor";

/**
 * The URL with every route PARAMETER's value put back as its name
 * (`/en/manage/menu/42` → `/:locale/manage/menu/:productId`). It identifies the
 * ROUTE a path matched without needing a data router's `useMatches` (the shell
 * is also rendered under a plain `MemoryRouter` in specs).
 */
export function routePatternKey(
  pathname: string,
  params: Readonly<Record<string, string | undefined>>,
): string {
  return Object.entries(params).reduce(
    (path, [name, value]) =>
      value ? path.replace(`/${value}`, `/:${name}`) : path,
    pathname,
  );
}

/** The ☰ glyph, as a constant so it is not a bare literal in markup. */
/**
 * The back-office shell — the layout route every `/manage/**` screen renders
 * inside.
 *
 * It exists because `/manage` is a dashboard, and `docs/design` governs those:
 * ☰ top bar + full-height drawer below 1024px, permanent sidebar at or above
 * it. `/pos` keeps its bottom nav under a documented carve-out (a till held in
 * one hand is not a dashboard shell) — see `docs/design/design-principles.md`.
 *
 * It owns the data (decision D-C): the session, the permission filtering and
 * the logout call live here, and both nav surfaces are handed a finished list
 * and a handler. That is also why logout finally exists at all — before this,
 * `AppNav` hid itself for any role with one permitted item, which left a WAITER
 * with no way to sign out of a shared till.
 *
 * Permission filtering is STRICT: no session means no items. There is
 * deliberately no "empty set means show everything" fallback — a fail-open on a
 * nav that reaches menu pricing and staff roles is the wrong way to fail.
 * Server-side `@RequirePermission` is still the real gate; this is orientation.
 */
// eslint-disable-next-line no-restricted-syntax -- app-local chrome, pending: the shared shell cannot be extracted until the platform settles ONE mobile navigation model (seller = bottom icon bar, restaurant = drawer). Tracked as step 5 of the parity audit; do not add another local chrome component meanwhile.
export function ManageShell() {
  const toast = usePrivateToast();
  const lifetime = useRef(capturePrivateSession()).current;
  const t = useTranslate();
  const { direction, locale: activeLocale } = useI18n();
  const params = useParams();
  const { locale = "ar" } = params;
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const errorMessage = useErrorMessage();
  const { data } = useSession();
  // OFFLINE: the page beneath goes `inert` while the overlay is up (see below).
  const { offline } = useNetworkStatus();
  const routeKey = routePatternKey(pathname, params);
  /*
   * PAGE LOADING. The page chunks are fetched ahead of the click (idle, then
   * eagerly on the first pointer-over / focus / touch of any link), and a route
   * that still has to wait keeps the OLD page up for 200ms before the box
   * replaces it — see `usePageLoadingBox` for why that is not a Suspense
   * fallback.
   */
  const loadingWidth = usePageLoadingBox();
  useEffect(() => prefetchPagesWhenIdle(), []);
  // A committed route is the end of any wait: a stale chunk that is still in
  // flight for a page the user already left must not raise a box over this one.
  useEffect(() => {
    pendingPage.clear();
  }, [routeKey]);
  const prefetchOnIntent = (event: SyntheticEvent) => {
    if ((event.target as Element).closest?.("a[href]")) prefetchPagesNow();
  };
  // Shared with the kitchen board, which lives outside this shell — see the
  // hook's own note for why it had no sign-out at all before.
  const { logout: onLogout, isLoggingOut } = useLogout();
  // The look the back office wears, on `<body>` for as long as this shell is
  // mounted — see the hook for why the body and why per device.
  useApplyDashboardTheme();
  // Light or dark, the device's one choice (the till's and this back office's):
  // dark is the same look in its dark palette (`paper.css`), switched from the
  // account menu.
  usePosTheme();
  // The switch names the OTHER language in that language — «English» on an
  // Arabic page, «العربية» on an English one — so it reads to the person who
  // needs it. Read from that locale's own catalog rather than a new key: the
  // English catalog may not carry Arabic text.
  const otherLocale = activeLocale === "ar" ? "en" : "ar";
  const otherLocaleName =
    otherLocale === "ar"
      ? catalogs.ar.shell.languageArabic
      : catalogs.en.shell.languageEnglish;
  const switchLocale = () =>
    navigate(`${pathname.replace(/^\/[^/]+/, `/${otherLocale}`)}${search}`);

  /*
   * The kitchen row reflects what the store RUNS, not only what the session
   * may see: a board nobody enabled is not a destination, and a nav row whose
   * page opens on "this is turned off" teaches people to stop trusting the
   * nav. The toggle is read from settings (`MENU_VIEW`, the same read the POS
   * uses for receipts). Unknown fails OPEN — while loading, on error, or for
   * a session without `MENU_VIEW` — because the board page carries its own
   * display-off explanation, and hiding a row on a flaky fetch would make the
   * nav appear to lose entries at random.
   */
  const canReadSettings = hasPermission(data, PERMISSIONS.MENU_VIEW);
  const settingsQuery = useApiQuery<RestaurantSettings>(
    queryKeys.settings,
    endpoints.restaurant.settings,
    { enabled: canReadSettings, retry: false },
  );
  const kitchenRowVisible = settingsQuery.data
    ? settingsQuery.data.kitchenDisplayEnabled
    : true;

  /**
   * The cash-drawer shift feature (D25). Exactly the same pattern as
   * `kitchenRowVisible`, read off the same settings fetch: a store that never
   * flipped `shiftTrackingEnabled` never sees a shift screen, and unknown
   * fails OPEN (loading, error, or a session without `MENU_VIEW`) for the
   * same reason — hiding a row on a flaky fetch would make the nav appear to
   * lose entries at random.
   */
  const shiftRowsVisible = settingsQuery.data
    ? settingsQuery.data.shiftTrackingEnabled
    : true;

  /*
   * No open/closed state lives here any more.
   *
   * It used to: the side drawer needed `openedAtPath` so a back-button
   * navigation or a widen past `lg` would close it. The rail's menu is owned by
   * `BottomNav` — it is part of the pill, dismisses on an outside press or
   * Escape, and is `lg:hidden` so a widen removes it outright. A shell that
   * tracks no UI state cannot leave any of it stale.
   */

  /*
   * The rows themselves are DATA, in `rows.ts`, and this only adds the labels.
   *
   * They used to be declared here, inline, because labelling them needs
   * `useTranslate` — and `ManageIndexRedirect` therefore kept a second,
   * hardcoded copy of five of them to decide where bare `/manage` lands. The
   * two drifted, as two lists do: every row the copy did not know about was a
   * role redirected onto a permission wall while its own row sat in this nav.
   * One list, labelled here and read there.
   */
  const items = useMemo<ManageNavItem[]>(
    () =>
      visibleManageRows(data?.seller.permissions, {
        kitchenDisplay: kitchenRowVisible,
        shiftTracking: shiftRowsVisible,
      }).map((row) => ({
        rowKey: row.key,
        to: `/${locale}/${row.path}`,
        // The row's key IS its `nav.*` message key, so there is no second
        // lookup table to keep in step with the first. PAPER names a few rows
        // as the redesign does (`navPaper.*`); the rest keep their own.
        label: row.path === "kitchen/availability"
          ? t((m) => m.kitchen.availabilityTitle)
          : row.key === "staff" && !holdsAdministrationPermission(data?.seller.permissions)
          ? t((m) => m.dispatch.driversTab)
          : isPaperNavKey(row.key)
          ? t((m) => m.navPaper[row.key as PaperNavKey])
          : t((m) => m.nav[row.key]),
        icon: row.icon,
        group: row.group,
      })),
    [data, locale, t, kitchenRowVisible, shiftRowsVisible],
  );

  // The top bar names the screen the user is on. Longest match wins so a future
  // detail route (`/manage/menu/:id`) still reads as "Menu" rather than falling
  // through to the app name.
  const activeItem = items
    .filter(
      (item) => pathname === item.to || pathname.startsWith(`${item.to}/`),
    )
    .sort((a, b) => b.to.length - a.to.length)[0];
  const pageTitle = activeItem?.label ?? t((m) => m.common.appName);
  // The top bar pairs the destination's own glyph with its label, as the seller
  // portal does — a title alone reads as a heading, not as "where you are".
  const ActiveGlyph = activeItem ? NavIcon[activeItem.icon] : null;

  /**
   * Store switching, the same call `ProtectedLayout` already makes on arrival —
   * the shared sidebar carries a switcher, and an owner with a restaurant and a
   * shop needs it here rather than only via a redirect.
   */
  const [switchingTo, setSwitchingTo] = useState<string | null>(null);
  /**
   * Whether the sidebar is collapsed.
   *
   * Owned HERE, not by the sidebar. It lived inside `ManageSidebar`, which
   * meant collapsing took the only control that could un-collapse it off the
   * screen — the nav was gone with no way back short of a reload. The seller
   * portal keeps the flag in its shell for exactly this reason, and grows an
   * expand button in the top bar while it is set.
   */
  const [sidebarHidden, setSidebarHidden] = useState(false);
  const onSwitchStore = (slug: string) => {
    assertPrivateSessionLifetime(lifetime);
    setSwitchingTo(slug);
    void switchPrivateStore(queryClient, slug)
      .catch((failure) => toast.error(errorMessage(failure)))
      .finally(() => setSwitchingTo(null));
  };

  const storeName = data?.seller.storeName ?? t((m) => m.common.appName);
  const userName = data?.seller.name;

  return (
    <PageActionProvider>
      <div
        className="app-shell"
        dir={direction}
        lang={activeLocale}
        onPointerOverCapture={prefetchOnIntent}
        onFocusCapture={prefetchOnIntent}
        onTouchStartCapture={prefetchOnIntent}
      >
        {/* Mobile top bar: the page name only.
          The hamburger is GONE. Navigation moved to the floating rail at the
          bottom — the platform's mobile model, and where the seller portal has
          always put it. A drawer at the top-start corner is the least reachable
          control on a phone held one-handed, which on a POS is the wrong corner
          for the thing staff touch most. The drawer still exists, opened from
          the rail's "more" control, as the overflow for the destinations the
          rail cannot hold. */}
        <header
          data-ui="topbar-mobile"
          className="no-print sticky top-0 z-30 flex items-center gap-2 border-b-[length:var(--border-card-width)] border-[color:var(--border)] bg-[color:var(--surface)] px-4 py-2 lg:hidden"
        >
          <span className="min-w-0 flex-1">
            <ShellPageTitle fallback={pageTitle} mobile />
          </span>
          <RestaurantNotificationBell />
          {/* The current page's primary action, collapsed to its icon and sat
            beside the title — the platform's mobile placement. The page
            publishes it through `PageActionSlot`; the shell only renders it. */}
          <MobilePageAction />
        </header>

        <div className="flex min-h-0 flex-1">
          <ManageSidebar
            storeName={storeName}
            userName={userName}
            storeSlug={data?.seller.storeSlug ?? ""}
            availableStores={data?.availableStores ?? []}
            onSwitchStore={onSwitchStore}
            isSwitchingStore={switchingTo !== null}
            items={items}
            canUseTill={(data?.seller.permissions ?? []).includes(
              PERMISSIONS.POS_USE,
            )}
            onLogout={onLogout}
            isLoggingOut={isLoggingOut}
            hidden={sidebarHidden}
            onToggleHidden={() => setSidebarHidden((value) => !value)}
          />
          {/* The main area takes the full remaining width; `ManagePage` centers
            the content container inside it, so the sidebar never decides where
            the content's start edge sits. */}
          <div className="flex min-w-0 flex-1 flex-col">
            {/* The desktop top bar, the seller portal's own: a 64px rule carrying
              the active destination. Below `lg` the mobile header above still
              runs, because that one also carries the page action. */}
            <header
              data-ui="topbar"
              className="no-print hidden h-16 shrink-0 items-center gap-3 border-b-[length:var(--border-card-width)] border-[color:var(--border)] bg-[color:var(--surface)] px-6 lg:flex"
            >
              {sidebarHidden ? (
                <button
                  type="button"
                  onClick={() => setSidebarHidden(false)}
                  aria-label={t((m) => m.nav.expand)}
                  data-testid="sidebar-expand"
                  className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-tile)] border border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--surface-alt)] hover:text-[color:var(--text)]"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <rect x="3" y="4" width="18" height="16" rx="2" />
                    <line x1="9" y1="4" x2="9" y2="20" />
                  </svg>
                </button>
              ) : null}
              {/*
              Title, and under it whatever the page wants to say about itself.
              The bar used to carry the title alone while the page below drew a
              second, larger copy of the same word with its own action beside
              it — so "Menu" appeared twice, one under the other. A page that
              hands its header up (`hideTitle`) gets its subtitle and its
              primary action rendered HERE instead, which is the artboard's
              shape and what this bar was always for.
            */}
              <div className="flex min-w-0 items-center gap-2">
                {ActiveGlyph ? (
                  <span
                    aria-hidden="true"
                    data-ui="topbar-glyph"
                    className="inline-flex shrink-0 text-[color:var(--text-secondary)] [&_svg]:h-[19px] [&_svg]:w-[19px]"
                  >
                    <ActiveGlyph />
                  </span>
                ) : null}
                <div className="min-w-0">
                  <div
                    data-ui="topbar-crumbs"
                    className="flex min-w-0 items-center gap-[5px] text-[13px]"
                  >
                    <span
                      data-ui="topbar-crumb-store"
                      className="shrink-0 text-[color:var(--text-muted)]"
                    >
                      {storeName}
                    </span>
                    <span
                      aria-hidden="true"
                      data-ui="topbar-crumb-sep"
                      className="shrink-0 text-[color:var(--text-disabled)]"
                    >
                      /
                    </span>
                    <ShellPageTitle fallback={pageTitle} />
                  </div>
                  <ShellPageSubtitle />
                </div>
              </div>

              <div className="ms-auto flex shrink-0 items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={switchLocale}
                  lang={otherLocale}
                  data-testid="topbar-language"
                  leadingIcon={
                    <Languages
                      size={15}
                      strokeWidth={1.75}
                      aria-hidden="true"
                    />
                  }
                >
                  {otherLocaleName}
                </Button>
                <RestaurantNotificationBell />
                <ShellPageAction />
              </div>
            </header>

            {/* The relative frame the OFFLINE pane lays over. The pane is a
            sibling of <main>, not a replacement for it: the page stays mounted
            (and `inert`) underneath, so nothing typed on it is lost. */}
            <div
              className="relative flex min-h-0 flex-1 flex-col"
              data-ui="main-frame"
            >
              <main
                inert={offline || Boolean(loadingWidth)}
                data-app-scroll
                data-ui="main"
                data-pending={loadingWidth ? "" : undefined}
                /* Bottom padding clears the floating bar (a 62px pill held 14px
             off the bottom, plus the home indicator's inset) with room to
             spare; it is only there below `lg`, which is exactly where the
             bar is. */
                className="app-main p-4 pb-[calc(6rem+env(safe-area-inset-bottom,0px))] lg:p-6 lg:pb-6"
              >
                {/* Route chunks load behind the box above (`page-pending-anchor`),
              not behind a Suspense fallback. The boundary is UN-keyed on
              purpose: React Router navigates in a transition, and a transition
              keeps the page already on screen while the next one's chunk
              arrives. The old page therefore stays put (and unjumbled) for the
              first 200ms; only then does the box fade in over it. The anchor is
              a zero-height strip at the top of <main> — the box draws in the
              same frame a page uses, so it starts where the page will start.
              Changing only a route param keeps the page mounted, as before
              (`routePatternKey` still names the route). */}
                {loadingWidth ? (
                  <PageLoadingAnchor width={loadingWidth} />
                ) : null}
                <Suspense fallback={null}>
                  <Outlet />
                </Suspense>
              </main>
              <OfflinePane />
            </div>
          </div>
        </div>

        <ManageBottomNav
          items={items}
          storeName={storeName}
          storeSlug={data?.seller.storeSlug ?? ""}
          availableStores={data?.availableStores ?? []}
          onSwitchStore={onSwitchStore}
          isSwitchingStore={switchingTo !== null}
          userName={userName}
          onLogout={onLogout}
          isLoggingOut={isLoggingOut}
        />
      </div>
    </PageActionProvider>
  );
}

/**
 * Renders whatever primary action the current page published. Split out so the
 * shell can read the context it also provides — a component cannot consume a
 * provider it renders itself.
 */
/**
 * The desktop bar's subtitle and action, as CHILDREN of the provider.
 *
 * They cannot be read in `ManageShell` itself: that component renders
 * `<PageActionProvider>` in its own JSX, so a `useContext` call in its body
 * runs above the provider it creates and only ever sees the default. Reading it
 * one level down is the same shape `MobilePageAction` already uses, and the
 * reason it works there.
 */
function ShellPageSubtitle() {
  const { subtitle } = useShellPageMeta();
  if (!subtitle) return null;
  return (
    <p
      data-ui="topbar-subtitle"
      className="min-w-0 truncate text-[11px] font-semibold text-[color:var(--text-muted)]"
    >
      {subtitle}
    </p>
  );
}

function ShellPageAction() {
  const { action, secondary } = useShellPageMeta();
  if (!action && !secondary) return null;
  return (
    <div
      data-ui="topbar-action"
      className="ms-auto flex shrink-0 items-center gap-2"
    >
      {/* Secondary BEFORE primary, which is the artboard's order and the
          platform's everywhere else. */}
      {secondary ? (
        <Link
          to={secondary.to}
          data-testid={secondary.testId}
          className="inline-flex h-[34px] items-center gap-1.5 rounded-[var(--radius-tile)] border border-[color:var(--border)] px-[13px] text-[11.5px] font-bold text-[color:var(--text-secondary)]"
        >
          {secondary.icon}
          {secondary.label}
        </Link>
      ) : null}
      {action ? (
        <Button
          variant="primary"
          size="sm"
          onClick={action.onClick}
          disabled={action.disabled}
          data-testid={action.testId}
        >
          {action.label}
        </Button>
      ) : null}
    </div>
  );
}

/**
 * The bar's page name: whatever the page published, else the nav item's label.
 *
 * A page reached THROUGH a sidebar item rather than named by one — anything
 * under `/manage/settings` — would otherwise be called "Settings" here while
 * carrying no heading of its own, so the seller would have no way to read which
 * settings screen they were on.
 */
function ShellPageTitle({
  fallback,
  mobile = false,
}: {
  fallback: string;
  mobile?: boolean;
}) {
  const { title } = useShellPageMeta();
  return (
    <h1
      data-ui="topbar-title"
      className={
        mobile
          ? "ui-display min-w-0 truncate text-base font-semibold"
          : "ui-display min-w-0 truncate text-[15px] font-semibold leading-tight"
      }
    >
      {title || fallback}
    </h1>
  );
}

function MobilePageAction() {
  const action = useMobilePageAction();
  if (!action) return null;
  return (
    <span className="flex shrink-0 items-center gap-1 rounded-full bg-[color:var(--soft2)] p-[5px]">
      <button
        type="button"
        onClick={action.onClick}
        disabled={action.disabled}
        aria-label={action.label}
        data-testid={action.testId}
        className="flex h-[30px] w-[33px] items-center justify-center rounded-full text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--soft)] disabled:opacity-50"
      >
        {action.icon}
      </button>
    </span>
  );
}
